import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { googleBigQueryAuthTypes } from 'app/model/linkedservice/google-bigquery.linkedservice';
import { format, getConnectorLegacyNameByFlag } from 'app/shared/utils/string.util';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { parameterizeDynamicsControlMetadata } from '../consts/metadata-consts';
import { EnvironmentInjector } from '@angular/core';
import { GlobalVariables } from 'app/services/globals';

export const userAuthPassControls: ControlMetadata[] = [
    {
        type: 'textbox',
        bind: 'clientId',
        required: true,
        label: stringResources.ModelName_ClientId,
        isLabelVertical: true,
        validators: [{ type: 'required', errorMessage: '' }]
    },
    {
        type: 'dynamic-akv-switcher',
        bind: 'clientSecret',
        required: true,
        label: stringResources.ModelName_ClientSecret,
        child: {
            type: 'password',
            required: true,
            label: stringResources.ModelName_ClientSecret,
            isLabelVertical: true
        },
        validators: [{ type: 'required', errorMessage: '' }],
        description: format(
            stringResources.AzureKeyVaultSecretName_Tooltip_Template,
            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.GoogleBigQuery_DisplayText),
            stringResources.ModelName_ClientSecret
        )
    },
    {
        type: 'dynamic-akv-switcher',
        required: true,
        bind: 'refreshToken',
        label: stringResources.ModelName_RefreshToken,
        child: {
            type: 'password',
            required: true,
            label: stringResources.ModelName_RefreshToken,
            isLabelVertical: true
        },
        validators: [{ type: 'required', errorMessage: '' }],
        description: format(
            stringResources.AzureKeyVaultSecretName_Tooltip_Template,
            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.GoogleBigQuery_DisplayText),
            stringResources.ModelName_RefreshToken
        )
    }
];

const serviceAuthControls: ControlMetadata[] = [
    {
        type: 'textbox',
        bind: 'email',
        label: stringResources.EmailLabel,
        isLabelVertical: true
    },
    {
        type: 'textbox',
        bind: 'keyFilePath',
        label: stringResources.ModelName_KeyFilePath,
        isLabelVertical: true
    },
    {
        type: 'textbox',
        bind: 'trustedCertPath',
        label: stringResources.ModelName_TrustedCertPath,
        isLabelVertical: true
    },
    {
        type: 'radio',
        bind: 'useSystemTrustStore',
        label: stringResources.ModelName_UseSystemTrustStore,
        isLabelVertical: true,
        options: [
            {
                label: stringResources.True,
                value: true
            },
            {
                label: stringResources.False,
                value: false
            }
        ],
        defaultValue: false
    }
];

const commonControls: ControlMetadata[] = [
    {
        type: 'textbox',
        bind: 'project',
        label: stringResources.ModelName_ProjectID,
        required: true,
        validators: [{ type: 'required', errorMessage: '' }],
        isLabelVertical: true
    },
    {
        type: 'textbox',
        bind: 'additionalProjects',
        label: stringResources.ModelName_AdditionalProjectIDs,
        isLabelVertical: true
    },
    {
        type: 'radio',
        bind: 'requestGoogleDriveScope',
        label: stringResources.ModelName_RequestGoogleDriveScope,
        isLabelVertical: true,
        options: [
            {
                label: stringResources.True,
                value: true
            },
            {
                label: stringResources.False,
                value: false
            }
        ],
        defaultValue: false
    }
];

export const googleBigQueryMetadata = async (injector: EnvironmentInjector) => {
    const result: ILinkedServiceTypeMetadata = {
        type: adfModelTypeMap.googleBigQuery.linkedServiceType,
        displayName: getConnectorLegacyNameByFlag(stringResources.GoogleBigQuery_DisplayText, true),
        disabled: __IS_FABRIC_DPE__,
        linkedServiceKind: 'table',
        icon: {
            className: 'svg-i-100x100-google_bigquery'
        },
        warningMessage: format(
            stringResources.Connector_Legacy_IsNotSupportedWarning_alt2,
            stringResources.LinkedServiceLowerCase,
            stringResources.GoogleBigQuery_DisplayText
        ),
        warningMessageHyperlink:
            'https://learn.microsoft.com/azure/data-factory/connector-google-bigquery?tabs=data-factory', // Need to update
        isFirstClass: true,
        supportFirstClassParameterExperience: true,
        category: ['Database'],
        supportDatasetV2: true,
        copySinkTypes: [],
        copySourceTypes: [adfModelTypeMap.googleBigQuery.copySourceType],
        notShowInTypeSelection: !GlobalVariables.showLegacy,
        isDeprecated: !GlobalVariables.showLegacy,
        controls: [
            ...commonControls.map(parameterizeDynamicsControlMetadata),
            {
                type: 'select-switch',
                bind: 'authenticationType',
                label: stringResources.ModelName_AuthenticationType,
                options: [
                    {
                        label: stringResources.ModelName_ServiceAuthentication,
                        value: googleBigQueryAuthTypes.ServiceAuthentication
                    },
                    {
                        label: stringResources.ModelName_UserAuthentication_NoPreview,
                        value: googleBigQueryAuthTypes.UserAuthentication
                    }
                ],
                required: true,
                validators: [{ type: 'required', errorMessage: '' }],
                isLabelVertical: true,
                defaultValue: googleBigQueryAuthTypes.UserAuthentication,
                cases: [
                    {
                        caseValue: googleBigQueryAuthTypes.ServiceAuthentication,
                        children: serviceAuthControls.map(parameterizeDynamicsControlMetadata)
                    },
                    {
                        caseValue: googleBigQueryAuthTypes.UserAuthentication,
                        children: userAuthPassControls.map(parameterizeDynamicsControlMetadata)
                    }
                ]
            }
        ]
    };
    return result;
};
